/**
 * Audit-Trail Ergebnis-Tabelle (Spec 530, Phase 2 + Phase 4).
 *
 * Zeigt die Events als Tabelle mit expandierbarem Detail. Phase 4 ergaenzt die
 * visuelle Hierarchie: Tier-Farbindikator (Tier 1 destructive, Tier 2 warning,
 * Tier 3 muted; mit Text-Label, kein reines Farbsignal), dezentes rotes
 * Row-Highlight fuer `denied`-Zeilen und das zweispaltige Vorher/Nachher-Diff
 * ({@link AuditDiffDisplay}).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Badge,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import type { AuditEvent, AuditResult, AuditTier } from '@/types/audit';
import { AuditDiffDisplay } from './audit-diff-display';

/** Badge-Variante je Ausgang: denied/error = destruktiv, success = neutral. */
function resultVariant(result: AuditResult): 'default' | 'destructive' | 'secondary' {
  if (result === 'denied' || result === 'error') return 'destructive';
  return 'secondary';
}

/** Tier-Farbklassen: Tier 1 destructive, Tier 2 warning, Tier 3 muted. */
function tierClasses(tier: AuditTier): string {
  switch (tier) {
    case 1:
      return 'border-destructive/40 bg-destructive/10 text-destructive';
    case 2:
      return 'border-warning/40 bg-warning/10 text-foreground';
    default:
      return 'border-transparent bg-muted text-muted-foreground';
  }
}

/** Tier-Indikator: farbige Pille mit Text-Label (zugaenglich, kein reines Farbsignal). */
function TierIndicator({ tier, label }: { tier: AuditTier; label: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
        tierClasses(tier),
      )}
      data-testid="audit-tier-indicator"
      data-tier={tier}
    >
      {label}
    </span>
  );
}

export function AuditTable({ events }: { events: AuditEvent[] }) {
  const { t } = useTranslation('audit');
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>{t('table.timestamp')}</TableHead>
              <TableHead>{t('table.actor')}</TableHead>
              <TableHead>{t('table.action')}</TableHead>
              <TableHead>{t('table.resource')}</TableHead>
              <TableHead>{t('table.result')}</TableHead>
              <TableHead>{t('table.tier')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.map((ev) => {
              const open = expanded === ev.eventId;
              return (
                <AuditRow
                  key={ev.eventId}
                  event={ev}
                  open={open}
                  onToggle={() => setExpanded(open ? null : ev.eventId)}
                />
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function AuditRow({
  event,
  open,
  onToggle,
}: {
  event: AuditEvent;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('audit');
  const denied = event.result === 'denied';
  return (
    <>
      <TableRow
        className={cn('cursor-pointer', denied && 'bg-destructive/10 hover:bg-destructive/15')}
        onClick={onToggle}
        data-testid="audit-row"
        data-denied={denied || undefined}
      >
        <TableCell>{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
        <TableCell className="whitespace-nowrap font-mono text-xs">{event.timestamp}</TableCell>
        <TableCell>{event.actorEmail}</TableCell>
        <TableCell className="font-mono text-xs">{event.action}</TableCell>
        <TableCell className="font-mono text-xs">{event.resourceType}#{event.resourceId}</TableCell>
        <TableCell><Badge variant={resultVariant(event.result)}>{t(`result.${event.result}`)}</Badge></TableCell>
        <TableCell><TierIndicator tier={event.tier} label={t('tier.label', { tier: event.tier })} /></TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={7} className="bg-muted/40">
            <div className="flex flex-col gap-3 p-2">
              <dl className="grid gap-2 text-sm md:grid-cols-2" data-testid="audit-detail">
                <Field label={t('detail.sourceIp')} value={event.sourceIp} />
                <Field label={t('detail.requestId')} value={event.requestId} />
                <Field label={t('detail.impactScope')} value={event.impactScope} />
                {event.actorMerchantId && <Field label={t('detail.actorMerchant')} value={event.actorMerchantId} />}
                {event.merchantId && <Field label={t('detail.merchant')} value={event.merchantId} />}
                {event.mfaMethod && <Field label={t('detail.mfaMethod')} value={event.mfaMethod} />}
                {event.justification && <Field label={t('detail.justification')} value={event.justification} />}
              </dl>
              <AuditDiffDisplay event={event} />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="break-all">{value}</dd>
    </div>
  );
}
